import { motion } from "framer-motion";
import { Phone, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BUSINESS, IMAGES } from "../../data/content";
import { MaskLine } from "./Reveal";

export const Hero = () => {
  const navigate = useNavigate();
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden bg-fc-navy text-white">
      {/* side van photo background with navy overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="F&C Air Conditioning branded service van"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(100deg, rgba(7,32,63,0.97) 0%, rgba(11,44,92,0.90) 40%, rgba(11,44,92,0.55) 68%, rgba(11,44,92,0.35) 100%)" }}
        />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-fc-navy/90 to-transparent" />
      </div>

      {/* concentric ring decoration (right) */}
      <div className="pointer-events-none absolute top-1/2 -right-48 -translate-y-1/2 z-0">
        <div className="w-[640px] h-[640px] rounded-full border-[72px] border-white/[0.045]" />
      </div>
      <div className="pointer-events-none absolute top-1/2 right-24 -translate-y-1/2 z-0 hidden lg:block">
        <div className="w-[360px] h-[360px] rounded-full border border-white/[0.07]" />
      </div>

      <div className="relative z-10 mx-auto w-[92%] max-w-[1280px] py-24 lg:py-40 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-fc-orange mb-6"
          >
            Class A Air Conditioning Contractor
          </motion.div>

          <h1 className="font-display font-bold tracking-tight leading-[0.95] text-[13vw] sm:text-6xl lg:text-[5.2rem]">
            <MaskLine delay={0.3}>Cooling &amp; Heating</MaskLine>
            <MaskLine delay={0.42}>You Can Count On.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-7 max-w-xl text-white/75 text-lg"
          >
            Professional HVAC solutions for residential and commercial customers — from
            sales and installation to service and repairs.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-request-btn"
              onClick={() => navigate("/contact")}
              className="group inline-flex items-center gap-2 bg-fc-orange text-white px-7 py-4 font-bold uppercase tracking-wide text-sm hover:brightness-95 transition-all"
            >
              Request Service
              <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </button>
            <a
              data-testid="hero-call-btn"
              href={BUSINESS.phoneHref}
              className="inline-flex items-center gap-2 border border-white/40 hover:border-white px-7 py-4 font-bold uppercase tracking-wide text-sm transition-colors"
            >
              <Phone size={16} /> Call {BUSINESS.phone}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-2 font-semibold text-[13px] text-white/70"
          >
            <span>Licensed &amp; Insured</span>
            <span>Residential &amp; Commercial</span>
            <span>Sales • Service • Repairs • Installation</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-4 relative hidden lg:flex lg:justify-end lg:items-end self-end pb-1"
        >
          <div className="bg-fc-orange text-white px-6 py-5 shadow-2xl">
            <div className="font-display text-xl font-bold leading-none">Class A Licensed</div>
            <div className="font-mono text-[10px] uppercase tracking-widest mt-1">{BUSINESS.license}</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
